import { FormFieldType } from '../components/FormFieldType';
import { GenericFieldProps } from '../components/GenericFormField';

/**
 * Generates initial values for editing forms from a given entity and fields configuration.
 * It replaces `null` or `undefined` entity values with appropriate defaults based on field types
 * and supports recursive handling for nested field configurations.
 *
 * @param entity The existing entity values, which may contain `null` or `undefined` values.
 * @param fieldsConfig The configuration object for form fields, specifying types and initial values.
 * @returns An object mapping field names to their processed initial values, suitable for Formik initialization.
 */
export function generateEditInitialValues(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  entity: Record<string, any> | undefined,
  fieldsConfig: Record<string, GenericFieldProps> | undefined
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Record<string, any> {
  if (!fieldsConfig) return {};

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const initialValues = Object.keys(fieldsConfig).reduce<Record<string, any>>(
    (acc, key) => {
      const fieldConfig = fieldsConfig[key];
      let fieldValue = entity?.[key];

      // Use fieldConfig's initialValue or a type-appropriate default for null/undefined values
      fieldValue = fieldValue ?? getEmptyValueForType(fieldConfig.type);

      // Recursively handle nested configurations
      if (fieldConfig.type === FormFieldType.Nested && fieldConfig.config) {
        const nestedEntity =
          fieldValue && typeof fieldValue === 'object' ? fieldValue : {};
        acc[key] = generateEditInitialValues(nestedEntity, fieldConfig.config);
      } else {
        acc[key] = fieldValue;
      }

      return acc;
    },
    {}
  );

  return initialValues;
}

/**
 * Determines the default empty value for a field based on its type.
 * This is used to provide appropriate fallback values for null or undefined entity fields.
 *
 * @param fieldType The type of the field from the form field configuration.
 * @returns The default empty value for the field type (e.g., null for text fields, false for checkboxes).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getEmptyValueForType(fieldType: FormFieldType): any {
  switch (fieldType) {
    case FormFieldType.TextField:
      return null;
    case FormFieldType.Select:
      return '';
    case FormFieldType.Checkbox:
      return false;
    case FormFieldType.Nested:
      return {};
    default:
      return '';
  }
}
